<template>
  <div class="layout-admin">

    <!-- ============ 목록 화면 ============ -->
    <div v-if="mode === 'list'">
      <div class="layout-admin__toolbar">
        <h2 class="layout-admin__h2">버스 좌석 레이어 관리</h2>
        <button type="button" class="btn btn--primary" @click="openNewLayout">+ 새 레이어</button>
      </div>

      <table class="layout-admin__table">
        <thead>
          <tr>
            <th>코드</th>
            <th>레이어명</th>
            <th>총좌석수</th>
            <th>사용여부</th>
            <th>수정일</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="l in layouts" :key="l.layoutId">
            <td>{{ l.layoutCode }}</td>
            <td>{{ l.layoutName }}</td>
            <td>{{ l.totalSeats }}석</td>
            <td>
              <span :class="['badge', l.useFlag === 'Y' ? 'badge--on' : 'badge--off']">
                {{ l.useFlag === 'Y' ? '사용중' : '중지' }}
              </span>
            </td>
            <td>{{ l.modDate ? l.modDate.slice(0, 10) : '-' }}</td>
            <td class="layout-admin__actions">
              <button type="button" class="btn btn--sm" @click="openEditLayout(l.layoutId)">수정</button>
              <button type="button" class="btn btn--sm" @click="duplicateLayout(l.layoutId)">복제</button>
              <button type="button" class="btn btn--sm" @click="toggleUseFlag(l)">
                {{ l.useFlag === 'Y' ? '사용중지' : '사용재개' }}
              </button>
              <button type="button" class="btn btn--sm btn--danger" @click="deleteLayout(l)">삭제</button>
            </td>
          </tr>
          <tr v-if="layouts.length === 0">
            <td colspan="6" class="layout-admin__empty">등록된 레이어가 없어요.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ============ 에디터 화면 ============ -->
    <div v-else>
      <div class="layout-admin__toolbar">
        <h2 class="layout-admin__h2">{{ editing.layoutId ? '레이어 수정' : '새 레이어' }}</h2>
        <div>
          <button type="button" class="btn" @click="mode = 'list'">목록으로</button>
          <button type="button" class="btn btn--primary" :disabled="saving" @click="save">
            {{ saving ? '저장 중...' : '저장' }}
          </button>
        </div>
      </div>

      <div class="layout-admin__field">
        <label>레이어코드</label>
        <input v-model="editing.layoutCode" type="text" placeholder="예: LO-40-2X2" />
      </div>

      <div class="layout-admin__field">
        <label>레이어명</label>
        <input v-model="editing.layoutName" type="text" placeholder="예: 49인승 4x11+5" />
      </div>

      <div class="layout-admin__row-adder">
        <label>행 추가 — 좌석수</label>
        <input v-model.number="newRowSeatCount" type="number" min="1" max="10" style="width:60px" />
        <button type="button" class="btn btn--sm" @click="addRow(newRowSeatCount)">행 추가</button>
        <span class="layout-admin__total">총 {{ totalSeatCount }}석</span>
      </div>

      <div class="layout-admin__grid">
        <div v-for="(row, rIdx) in rows" :key="rIdx" class="layout-admin__row">
          <span class="layout-admin__row-no">{{ rIdx + 1 }}열</span>
          <template v-for="seat in row" :key="seat.seatNo">
            <button
              type="button"
              class="layout-admin__seat"
              :class="['seat--' + seat.seatGrade, { 'seat--active': selectedSeats.includes(seat) }]"
              @click="toggleSeatSelection(seat)"
            >
              {{ seat.seatNo }}
              <span v-if="seat.extraFee > 0" class="layout-admin__fee-dot">+</span>
            </button>
            <span v-if="seat.aisleAfter === 'Y'" class="layout-admin__aisle" />
          </template>
          <button type="button" class="btn btn--sm" :disabled="rIdx === 0" @click="moveRow(rIdx, -1)">↑</button>
          <button type="button" class="btn btn--sm" :disabled="rIdx === rows.length - 1" @click="moveRow(rIdx, 1)">↓</button>
          <button type="button" class="btn btn--sm" @click="selectRow(row)">행 선택</button>
          <button type="button" class="btn btn--sm btn--danger" @click="removeRow(rIdx)">행 삭제</button>
        </div>
        <p v-if="rows.length === 0" class="layout-admin__empty">행을 추가해서 좌석배치를 만들어주세요.</p>
      </div>

      <div class="layout-admin__legend">
        <span class="legend-item"><i class="dot seat--normal" />지정가능</span>
        <span class="legend-item"><i class="dot seat--blocked" />지정불가</span>
        <span class="legend-item"><i class="dot seat--memberOnly" />유료회원전용</span>
        <span class="legend-item"><i class="dot seat--preAssign" />사전예약석</span>
      </div>

      <!-- 선택된 좌석 일괄 설정 패널 -->
      <div v-if="selectedSeats.length" class="layout-admin__panel">
        <div class="layout-admin__panel-header">
          <strong>{{ selectedSeats.length }}개 좌석 선택됨</strong>
          <button type="button" class="btn btn--sm" @click="clearSelection">선택 해제</button>
        </div>

        <div class="layout-admin__field">
          <label>등급 일괄 적용</label>
          <select v-model="bulkGrade" @change="applyBulkGrade">
            <option value="">선택하세요</option>
            <option value="normal">지정가능</option>
            <option value="blocked">지정불가</option>
            <option value="memberOnly">유료회원전용</option>
            <option value="preAssign">사전예약석</option>
          </select>
        </div>

        <div class="layout-admin__field">
          <label>추가요금 일괄 적용(원)</label>
          <div class="layout-admin__inline-group">
            <input v-model.number="bulkFee" type="number" min="0" step="1000" />
            <button type="button" class="btn btn--sm" @click="applyBulkFee">적용</button>
          </div>
        </div>

        <div class="layout-admin__field layout-admin__field--inline">
          <label>통로</label>
          <div class="layout-admin__inline-group">
            <button type="button" class="btn btn--sm" @click="applyBulkAisle('Y')">뒤에 통로 켜기</button>
            <button type="button" class="btn btn--sm" @click="applyBulkAisle('N')">통로 끄기</button>
          </div>
        </div>

        <button type="button" class="btn btn--sm btn--danger" @click="removeSelectedSeats">선택 좌석 전체 삭제</button>
      </div>

      <!-- ============ 적용상품 연동 (레이어 저장 후 설정) ============ -->
      <section class="layout-admin__target">
        <div class="layout-admin__target-head">
          <h3 class="layout-admin__h3">적용상품 연동</h3>
          <button
            v-if="editing.layoutId"
            type="button"
            class="btn btn--primary btn--sm"
            :disabled="targetSaving"
            @click="saveTargets"
          >
            {{ targetSaving ? '저장 중...' : '연동정보 저장' }}
          </button>
        </div>

        <p v-if="!editing.layoutId" class="layout-admin__hint">
          레이어를 먼저 저장하면 적용상품을 연동할 수 있어요.
        </p>

        <template v-else>
          <p class="layout-admin__hint">
            이 레이어(버스)를 사용할 상품 대상을 설정해요. 해당 상품의 결제 과정에서 구매자가 좌석을 지정할 수 있어요. 대상은 여러 개 선택할 수 있어요.
          </p>

          <div class="layout-admin__target-block">
            <div class="layout-admin__target-label">상품카테고리</div>
            <label v-for="c in categoryOptions" :key="c.value" class="layout-admin__chk">
              <input
                type="checkbox"
                :checked="targets.categories.includes(c.value)"
                @change="toggleCategory(c.value)"
              />
              {{ c.label }}
            </label>
          </div>

          <div class="layout-admin__target-block">
            <div class="layout-admin__target-label">상품판매처</div>
            <label v-for="ch in channelOptions" :key="ch.value" class="layout-admin__chk">
              <input
                type="checkbox"
                :checked="targets.channels.includes(ch.value)"
                @change="toggleChannel(ch.value)"
              />
              {{ ch.label }}
            </label>
          </div>

          <div class="layout-admin__target-block">
            <div class="layout-admin__target-label">상품코드</div>
            <div class="layout-admin__code-add">
              <input
                v-model="productInput"
                type="text"
                placeholder="상품코드 입력 (여러 개는 쉼표 또는 공백으로 구분)"
                @keyup.enter="addProducts"
              />
              <button type="button" class="btn btn--sm" :disabled="productChecking" @click="addProducts">
                {{ productChecking ? '확인 중...' : '추가' }}
              </button>
            </div>
            <ul v-if="targets.products.length" class="layout-admin__chips">
              <li v-for="(p, i) in targets.products" :key="p.code" class="layout-admin__chip">
                <b>{{ p.code }}</b>
                <span>{{ p.name }}</span>
                <button type="button" title="삭제" @click="removeProduct(i)">×</button>
              </li>
            </ul>
            <p v-else class="layout-admin__hint">추가된 상품코드가 없어요.</p>
          </div>
        </template>
      </section>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Ma_BusLayoutAdmin',

  data() {
    return {
      mode: 'list', // 'list' | 'edit'
      layouts: [],
      editing: { layoutId: null, layoutCode: '', layoutName: '' },
      rows: [], // [[seat, seat, ...], [seat, ...], ...]
      selectedSeats: [],
      bulkGrade: '',
      bulkFee: 0,
      newRowSeatCount: 4,
      saving: false,

      // 적용상품 연동 (상품카테고리 / 상품판매처 / 상품코드 - 각각 여러 개 선택 가능)
      targets: { categories: [], channels: [], products: [] }, // products: [{ code, name }]
      productInput: '',
      productChecking: false,
      targetSaving: false,
      categoryOptions: [
        { value: 'ALL', label: '전체(ALL)' },
        { value: 'L', label: 'L 버스투어' },
        { value: 'T', label: 'T 패키지' },
        { value: 'F', label: 'F 선박상품' },
        { value: 'E', label: 'E' },
      ],
      channelOptions: [
        { value: 'HOME', label: '홈페이지' },
        { value: 'OTHER', label: '기타 판매처' },
      ],
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
    totalSeatCount() {
      return this.rows.reduce((sum, row) => sum + row.length, 0);
    },
  },

  created() {
    this.user = this.$store.state.user;
    this.loadLayouts();
  },

  mounted() {
    // 권한없는 로그인 차단
    if (this.user.handlerGrade >= 20 || this.user.handlerGrade == undefined) {
      var swal = require('sweetalert2');
      swal.fire({
        title: '로그인 필요',
        text: '접근권한 확인불가.',
        icon: 'error'
      });
      this.$router.push({ path: '/' });
    }

    if (this.user.handlerId != undefined) {
      document.getElementById("admin").style.display = "block";
    }
    document.getElementById("user").style.display = "none";
  },

  methods: {
    async loadLayouts() {
      const res = await this.$api('/api/getBusLayoutList', { param: [] });
      if (Array.isArray(res)) this.layouts = res;
    },

    openNewLayout() {
      this.editing = { layoutId: null, layoutCode: '', layoutName: '' };
      this.rows = [];
      this.selectedSeats = [];
      this.targets = { categories: [], channels: [], products: [] };
      this.productInput = '';
      this.mode = 'edit';
    },

    async openEditLayout(layoutId) {
      const layout = this.layouts.find((l) => l.layoutId === layoutId);
      const seatRes = await this.$api('/api/getBusLayoutSeats', { param: [layoutId] });

      this.editing = { layoutId, layoutCode: layout ? layout.layoutCode : '', layoutName: layout ? layout.layoutName : '' };
      this.rows = this.buildRows(seatRes);
      this.selectedSeats = [];
      this.productInput = '';
      this.mode = 'edit';
      this.loadTargets(layoutId);
    },

    // 좌석 조회결과(평면 배열)를 화면용 2차원 배열(행 → 좌석)로 변환 (수정/복제 공용)
    buildRows(seatRes) {
      const rows = [];
      if (Array.isArray(seatRes)) {
        seatRes.forEach((seat) => {
          const rIdx = seat.seatRow - 1;
          if (!rows[rIdx]) rows[rIdx] = [];
          rows[rIdx][seat.seatOrder - 1] = seat;
        });
      }
      return rows;
    },

    // 레이어복제 — 좌석배치(등급/추가요금/통로 포함)를 그대로 가져와 "새 레이어" 상태로 편집기를 엽니다.
    // 이 시점엔 DB에 저장되지 않고, [저장]을 눌러야 신규 레이어로 등록돼요.
    // - 레이어코드는 유니크라서 비워둡니다 (새 코드를 입력해야 저장돼요)
    // - 적용상품 연동은 복사하지 않아요 (같은 대상이 두 레이어에 걸리는 것을 방지)
    async duplicateLayout(layoutId) {
      const layout = this.layouts.find((l) => l.layoutId === layoutId);
      const seatRes = await this.$api('/api/getBusLayoutSeats', { param: [layoutId] });
      if (!layout || !Array.isArray(seatRes) || seatRes.length === 0) {
        alert('복제할 레이어 정보를 불러오지 못했어요.');
        return;
      }

      this.editing = {
        layoutId: null,
        layoutCode: '',
        layoutName: `${layout.layoutName}`.slice(0, 43) + ' (복사)', // layoutName은 VARCHAR(50)
      };
      this.rows = this.buildRows(seatRes);
      this.selectedSeats = [];
      this.targets = { categories: [], channels: [], products: [] };
      this.productInput = '';
      this.mode = 'edit';
    },

    addRow(seatCount) {
      const count = Math.max(1, Math.min(10, Number(seatCount) || 4));
      const row = [];
      for (let i = 1; i <= count; i += 1) {
        row.push({
          seatNo: '', // renumberSeats()에서 채움
          seatRow: 0,
          seatOrder: i,
          // 4석이면 2+2로 보이도록 가운데(2번째 자리)에 기본 통로를 넣어줌. 그 외엔 없음 - 필요시 좌석 클릭해서 조정
          aisleAfter: count === 4 && i === 2 ? 'Y' : 'N',
          seatGrade: 'normal',
          extraFee: 0,
        });
      }
      this.rows.push(row);
      this.renumberSeats();
    },

    removeRow(rIdx) {
      const removed = this.rows[rIdx];
      this.selectedSeats = this.selectedSeats.filter((s) => !removed.includes(s));
      this.rows.splice(rIdx, 1);
      this.renumberSeats();
    },

    moveRow(rIdx, dir) {
      const target = rIdx + dir;
      if (target < 0 || target >= this.rows.length) return;
      const rows = this.rows;
      [rows[rIdx], rows[target]] = [rows[target], rows[rIdx]];
      this.renumberSeats();
    },

    renumberSeats() {
      this.rows.forEach((row, rIdx) => {
        row.forEach((seat, sIdx) => {
          seat.seatRow = rIdx + 1;
          seat.seatOrder = sIdx + 1;
          seat.seatNo = `${rIdx + 1}${String.fromCharCode(64 + sIdx + 1)}`; // 1 -> A, 2 -> B ...
        });
      });
    },

    selectRow(row) {
      row.forEach((seat) => {
        if (!this.selectedSeats.includes(seat)) this.selectedSeats.push(seat);
      });
    },

    toggleSeatSelection(seat) {
      const idx = this.selectedSeats.indexOf(seat);
      if (idx === -1) this.selectedSeats.push(seat);
      else this.selectedSeats.splice(idx, 1);
    },

    clearSelection() {
      this.selectedSeats = [];
      this.bulkGrade = '';
      this.bulkFee = 0;
    },

    applyBulkGrade() {
      if (!this.bulkGrade) return;
      this.selectedSeats.forEach((s) => { s.seatGrade = this.bulkGrade; });
    },

    applyBulkFee() {
      this.selectedSeats.forEach((s) => { s.extraFee = Number(this.bulkFee) || 0; });
    },

    applyBulkAisle(value) {
      this.selectedSeats.forEach((s) => { s.aisleAfter = value; });
    },

    removeSelectedSeats() {
      this.selectedSeats.forEach((seat) => {
        for (const row of this.rows) {
          const idx = row.indexOf(seat);
          if (idx !== -1) {
            row.splice(idx, 1);
            break;
          }
        }
      });
      this.rows = this.rows.filter((row) => row.length > 0);
      this.selectedSeats = [];
      this.renumberSeats();
    },

    // ---- 적용상품 연동 ----
    async loadTargets(layoutId) {
      this.targets = { categories: [], channels: [], products: [] };
      const res = await this.$api('/api/getBusLayoutTargets', { param: [layoutId] });
      if (!Array.isArray(res)) return;
      res.forEach((t) => {
        if (t.targetType === 'CATEGORY') this.targets.categories.push(t.targetValue);
        else if (t.targetType === 'CHANNEL') this.targets.channels.push(t.targetValue);
        else if (t.targetType === 'PRODUCT') {
          this.targets.products.push({ code: t.targetValue, name: t.productName || '(상품명 확인불가)' });
        }
      });
    },

    // 'ALL'(전체)과 개별 카테고리(L/T/F/E)는 함께 선택할 수 없게 함
    toggleCategory(value) {
      const list = this.targets.categories;
      const idx = list.indexOf(value);
      if (idx !== -1) {
        list.splice(idx, 1);
        return;
      }
      this.targets.categories = value === 'ALL'
        ? ['ALL']
        : [...list.filter((v) => v !== 'ALL'), value];
    },

    toggleChannel(value) {
      const list = this.targets.channels;
      const idx = list.indexOf(value);
      if (idx === -1) list.push(value);
      else list.splice(idx, 1);
    },

    // 상품코드 추가 - 쉼표/공백으로 여러 개 입력 가능, 존재하는 상품코드만 추가(오타 방지)
    async addProducts() {
      const codes = this.productInput.split(/[\s,]+/).filter(Boolean);
      if (codes.length === 0) return;

      this.productChecking = true;
      const notFound = [];
      try {
        for (const code of codes) {
          if (this.targets.products.some((p) => p.code === code)) continue;
          const res = await this.$api('/api/getProductNameByCode', { param: [code] });
          if (Array.isArray(res) && res.length > 0) {
            const found = res[0];
            if (!this.targets.products.some((p) => p.code === found.pdtCode)) {
              this.targets.products.push({ code: found.pdtCode, name: found.pdtNameKor });
            }
          } else {
            notFound.push(code);
          }
        }
      } finally {
        this.productChecking = false;
      }

      if (notFound.length > 0) {
        alert(`존재하지 않는 상품코드예요: ${notFound.join(', ')}`);
      }
      this.productInput = notFound.join(', '); // 못 찾은 코드만 남겨서 다시 고칠 수 있게 함
    },

    removeProduct(idx) {
      this.targets.products.splice(idx, 1);
    },

    async saveTargets() {
      const list = [
        ...this.targets.categories.map((v) => ({ targetType: 'CATEGORY', targetValue: v })),
        ...this.targets.channels.map((v) => ({ targetType: 'CHANNEL', targetValue: v })),
        ...this.targets.products.map((p) => ({ targetType: 'PRODUCT', targetValue: p.code })),
      ];

      this.targetSaving = true;
      try {
        const res = await this.$api('/api/proc_saveBusLayoutTargets', {
          param: [this.editing.layoutId, JSON.stringify(list), this.user.userId],
        });
        // CALL 응답은 드라이버에 따라 [{layoutId}] 또는 [[{layoutId}], OkPacket] 형태라서 둘 다 처리
        const first = Array.isArray(res) ? res[0] : null;
        const row = Array.isArray(first) ? first[0] : first;
        if (row && row.layoutId) {
          alert('연동정보를 저장했어요.');
        } else {
          console.log('saveBusLayoutTargets 응답:', res);
          alert('저장에 실패했어요: ' + (res && res.error ? res.error : '알 수 없는 오류'));
        }
      } finally {
        this.targetSaving = false;
      }
    },

    async toggleUseFlag(layout) {
      const nextFlag = layout.useFlag === 'Y' ? 'N' : 'Y';
      const res = await this.$api('/api/toggleBusLayoutUseFlag', { param: [nextFlag, layout.layoutId] });
      if (res && res.affectedRows === 1) {
        layout.useFlag = nextFlag;
      }
    },

    // 레이어삭제 — 소프트삭제(viewFlag='N'). 데이터는 DB에 남고 목록에서만 사라져요.
    // 이미 만들어진 회차/버스의 좌석정보에는 영향이 없어요.
    // 아직 사용 중인 루트가 있으면 삭제를 막아요 (루트가 삭제된 레이어를 가리키는 것을 방지).
    async deleteLayout(layout) {
      const swal = require('sweetalert2');

      const usage = await this.$api('/api/getBusLayoutUsage', { param: [layout.layoutId] });
      if (!Array.isArray(usage) || !usage[0]) {
        alert('사용 현황을 확인하지 못했어요. 잠시 후 다시 시도해주세요.');
        return;
      }
      const routeCnt = Number(usage[0].routeCnt) || 0;
      if (routeCnt > 0) {
        swal.fire({
          title: '삭제할 수 없어요',
          text: `이 레이어를 사용 중인 루트가 ${routeCnt}건 있어요. 루트의 좌석 레이어를 먼저 변경한 뒤 삭제해주세요.`,
          icon: 'warning',
        });
        return;
      }

      const ask = await swal.fire({
        title: '레이어 삭제',
        text: `'${layout.layoutName}' 레이어를 목록에서 삭제할까요? 이미 만들어진 회차의 좌석정보에는 영향이 없어요.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '삭제',
        cancelButtonText: '취소',
      });
      if (!ask.isConfirmed) return;

      const res = await this.$api('/api/deleteBusLayout', { param: [this.user.userId, layout.layoutId] });
      if (res && res.affectedRows === 1) {
        await this.loadLayouts();
      } else {
        alert('삭제에 실패했어요: ' + (res && res.error ? res.error : '알 수 없는 오류'));
      }
    },

    async save() {
      if (!this.editing.layoutCode) {
        alert('레이어코드를 입력해주세요.');
        return;
      }
      if (!this.editing.layoutName) {
        alert('레이어명을 입력해주세요.');
        return;
      }
      if (this.totalSeatCount === 0) {
        alert('좌석을 1개 이상 추가해주세요.');
        return;
      }
      this.renumberSeats();
      const seats = this.rows.flat();
      const wasNew = !this.editing.layoutId;

      this.saving = true;
      try {
        const res = await this.$api('/api/proc_saveBusLayout', {
          param: [
            this.editing.layoutId,
            this.editing.layoutCode,
            this.editing.layoutName,
            JSON.stringify(seats.map((s) => ({
              seatNo: s.seatNo,
              seatRow: s.seatRow,
              seatOrder: s.seatOrder,
              aisleAfter: s.aisleAfter,
              seatGrade: s.seatGrade,
              extraFee: s.extraFee,
            }))),
            this.user.userId,
          ],
        });
        // CALL 응답은 드라이버에 따라 [{layoutId}] 또는 [[{layoutId}], OkPacket] 형태라서 둘 다 처리합니다.
        const first = Array.isArray(res) ? res[0] : null;
        const row = Array.isArray(first) ? first[0] : first;
        if (row && row.layoutId) {
          await this.loadLayouts();
          if (wasNew) {
            // 신규 레이어는 저장 직후 편집화면에 머물러서, 바로 아래에서 적용상품을 연동할 수 있게 함
            this.editing.layoutId = row.layoutId;
            alert('레이어를 저장했어요. 아래에서 적용상품을 연동할 수 있어요.');
          } else {
            this.mode = 'list';
          }
        } else {
          console.log('saveBusLayout 응답:', res);
          const errMsg = res && res.error ? res.error : '';
          if (errMsg.includes('tb_bustour_layout_m_code_UK')) {
            alert('이미 사용 중인 레이어코드예요. (삭제한 레이어에서 쓰던 코드도 포함돼요) 다른 코드를 입력해주세요.');
          } else if (errMsg.includes('tb_bustour_layout_m_name_UK')) {
            alert('이미 같은 이름으로 사용 중인 레이어가 있어요. 다른 이름을 입력해주세요.');
          } else {
            alert('저장에 실패했어요: ' + (errMsg || '알 수 없는 오류'));
          }
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.layout-admin {
  padding: 20px;
  width: 80%;
  margin: 0 auto;
}

.layout-admin__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.layout-admin__h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.layout-admin__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.layout-admin__table th,
.layout-admin__table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e2e2;
  text-align: left;
}
.layout-admin__actions {
  display: flex;
  gap: 6px;
}
.layout-admin__empty {
  text-align: center;
  color: #999;
  padding: 24px;
}

.badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
}
.badge--on {
  background: #e1f5ee;
  color: #0f6e56;
}
.badge--off {
  background: #f1efe8;
  color: #5f5e5a;
}

.btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
}
.btn--primary {
  background: #2f7d5f;
  border-color: #2f7d5f;
  color: #fff;
}
.btn--primary:disabled {
  background: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
}
.btn--sm {
  padding: 4px 10px;
  font-size: 12px;
}
.btn--danger {
  color: #d64545;
  border-color: #f0c6c6;
}

.layout-admin__field {
  margin-bottom: 14px;
}
.layout-admin__field label {
  display: block;
  font-size: 12px;
  color: #777;
  margin-bottom: 4px;
}
.layout-admin__field input,
.layout-admin__field select {
  padding: 6px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  font-size: 13px;
}
.layout-admin__field--inline label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #333;
}
.layout-admin__inline-group {
  display: flex;
  gap: 6px;
}
.layout-admin__inline-group input {
  flex: 1;
}

.layout-admin__row-adder {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e2e2e2;
}
.layout-admin__row-adder label {
  font-size: 12px;
  color: #777;
}
.layout-admin__total {
  margin-left: auto;
  font-size: 13px;
  color: #555;
}

.layout-admin__grid {
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}
.layout-admin__row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.layout-admin__row-no {
  font-size: 11px;
  color: #999;
  width: 28px;
}
.layout-admin__seat {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  font-size: 11px;
  cursor: pointer;
  border: 1px solid #d0d0d0;
  background: #fff;
}
.layout-admin__seat.seat--active {
  outline: 2px solid #2f7d5f;
  outline-offset: 1px;
}
.seat--normal { background: #fff; color: #555; }
.seat--blocked { background: #444441; color: #fff; border-color: #444441; }
.seat--memberOnly { background: #faeeda; color: #854f0b; border-color: #f2cf94; }
.seat--preAssign { background: #eeedfe; color: #534ab7; border-color: #cecbf6; }
.layout-admin__fee-dot {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #d64545;
  color: #fff;
  font-size: 9px;
  line-height: 12px;
}
.layout-admin__aisle {
  width: 16px;
}

.layout-admin__legend {
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #666;
}
.dot {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  display: inline-block;
  border: 1px solid #d0d0d0;
}

.layout-admin__panel {
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  padding: 16px;
  max-width: 300px;
}
.layout-admin__panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

/* ---- 적용상품 연동 ---- */
.layout-admin__target {
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
}
.layout-admin__target-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.layout-admin__h3 {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
}
.layout-admin__hint {
  font-size: 12px;
  color: #999;
  margin: 0 0 12px;
}
.layout-admin__target-block {
  padding: 12px 0;
  border-top: 1px solid #f0f0f0;
}
.layout-admin__target-label {
  font-size: 12px;
  color: #777;
  margin-bottom: 8px;
}
.layout-admin__chk {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-right: 16px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
}
.layout-admin__code-add {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}
.layout-admin__code-add input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  font-size: 13px;
}
.layout-admin__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  list-style: none;
  padding: 0;
  margin: 0;
}
.layout-admin__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 10px;
  border-radius: 14px;
  background: #f1efe8;
  font-size: 12px;
  color: #444;
}
.layout-admin__chip span {
  color: #777;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.layout-admin__chip button {
  border: none;
  background: transparent;
  color: #999;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
}
</style>